import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getDistrictUrl, getBaseDomain } from '../../utils/domainUtils';
import LoadingState from '../shared/LoadingState';
import EmptyState from '../shared/EmptyState';
import './DistrictsList.css';

const DistrictsList = () => {
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchDistricts();
  }, []);

  const fetchDistricts = async () => {
    try {
      const response = await axios.get('/api/districts');
      setDistricts(response.data);
    } catch (error) {
      console.error('Failed to fetch districts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/districts/${id}`);
      setDistricts(districts.filter(d => d.id !== id));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete district:', error);
      alert('Failed to delete district');
    }
  };

  if (loading) {
    return <LoadingState message="Loading districts..." fullScreen />;
  }

  return (
    <div className="districts-list">
      <div className="page-header">
        <h2>Districts</h2>
        <Link to="/superadmin/districts/add" className="btn-primary">Add District</Link>
      </div>

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete District</h3>
            <p>Are you sure you want to delete this district? This action cannot be undone.</p>
            <div className="modal-actions">
              <button onClick={() => handleDelete(deleteConfirm)} className="btn-danger">Delete</button>
              <button onClick={() => setDeleteConfirm(null)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {districts.length === 0 ? (
        <EmptyState
          icon="🏫"
          title="No Districts Yet"
          message="Create your first district to get started. Each district will have its own subdomain and can manage its own forms and users."
          action={<Link to="/superadmin/districts/add" className="btn-primary">Add District</Link>}
        />
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>District Name</th>
                <th>Subdomain</th>
                <th>Contact Email</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {districts.map(district => (
                <tr key={district?.id}>
                  <td className="text-truncate">{district?.district_name || ''}</td>
                  <td>
                    <a 
                      href={getDistrictUrl(district?.subdomain)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-truncate"
                    >
                      {district?.subdomain || ''}.{getBaseDomain()}
                    </a>
                  </td>
                  <td className="text-truncate">{district?.contact_email || ''}</td>
                  <td className="actions">
                    <Link to={`/superadmin/districts/edit/${district?.id}`} className="btn-edit">Edit</Link>
                    <Link 
                      to={`/superadmin/districts/${district?.id}/admins`} 
                      className="btn-add-user"
                    >
                      Add User
                    </Link>
                    <Link 
                      to={`/superadmin/districts/${district?.id}/admins`} 
                      className="btn-list-admins"
                    >
                      List Users
                    </Link>
                    <Link 
                      to={`/superadmin/districts/${district?.id}/form-instances`} 
                      className="btn-edit"
                    >
                      View Forms
                    </Link>
                    <button 
                      onClick={() => setDeleteConfirm(district?.id)} 
                      className="btn-delete"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DistrictsList;

